import { Component, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { MessageCircle } from "lucide-react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import { CONTACTS } from "./data/site";
import Home from "./pages/Home";
import Concept from "./pages/Concept";
import Wedding from "./pages/Wedding";
import FestePrivate from "./pages/FestePrivate";
import EventiAziendali from "./pages/EventiAziendali";
import EventiPubblici from "./pages/EventiPubblici";
import CocktailServizi from "./pages/CocktailServizi";
import Allestimenti from "./pages/Allestimenti";
import Partner from "./pages/Partner";
import Gallery from "./pages/Gallery";
import Contatti from "./pages/Contatti";
import Privacy from "./pages/Privacy";
import Preventivo from "./pages/Preventivo";
import Admin from "./pages/Admin";

class ErrorBoundary extends Component {
  state = { err: false };
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err)
      return (
        <div className="min-h-screen grid place-items-center bg-brand-ink text-center p-8">
          <div>
            <p className="font-display text-4xl">Ops, qualcosa è andato storto.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-8 rounded-full bg-brand-orange px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink"
            >
              Ricarica
            </button>
          </div>
        </div>
      );
    return this.props.children;
  }
}

function ScrollManager({ lenisRef }) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, lenisRef]);
  return null;
}

function MobileCTA() {
  const { pathname } = useLocation();
  const isPubblici = pathname === "/eventi-pubblici";
  return (
    <div
      data-testid="mobile-persistent-cta"
      className="fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-white/10 bg-brand-ink/90 backdrop-blur-xl px-4 py-3 flex gap-3"
    >
      <Link
        to={isPubblici ? "/partner" : "/contatti"}
        data-testid="mobile-cta-quote"
        className="flex-1 rounded-full bg-brand-orange text-brand-ink text-[11px] font-semibold uppercase tracking-[0.18em] text-center py-3.5"
      >
        {isPubblici ? "Raccontaci il tuo evento" : "Richiedi un preventivo"}
      </Link>
      <a
        href={CONTACTS.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mobile-cta-whatsapp"
        aria-label="Scrivici su WhatsApp"
        className="inline-flex items-center justify-center rounded-full border border-white/20 px-4 text-brand-orange"
      >
        <MessageCircle size={18} />
      </a>
    </div>
  );
}

function VisitTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ page: pathname }),
      keepalive: true,
    }).catch(() => {});
  }, [pathname]);
  return null;
}

function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf = requestAnimationFrame(function loop(t) {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollManager lenisRef={lenisRef} />
        <div className="grain min-h-screen flex flex-col bg-brand-ink">
          <Navbar />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/concept" element={<Concept />} />
              <Route path="/wedding" element={<Wedding />} />
              <Route path="/feste-private" element={<FestePrivate />} />
              <Route path="/eventi-aziendali" element={<EventiAziendali />} />
              <Route path="/eventi-pubblici" element={<EventiPubblici />} />
              <Route path="/cocktail-e-servizi" element={<CocktailServizi />} />
              <Route path="/allestimenti" element={<Allestimenti />} />
              <Route path="/partner" element={<Partner />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contatti" element={<Contatti />} />
              <Route path="/preventivo" element={<Preventivo />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/eventi" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <VisitTracker />
          <Footer />
          <div className="h-[68px] md:hidden" aria-hidden="true" />
          <MobileCTA />
          <CookieBanner />
          <Toaster position="top-center" />
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
